import _get from 'lodash/get';
import { Widget } from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import WidgetFieldNames from '@lib/enums/fieldNames/widgetFieldNames';
import { prepareAttachedField } from '@lib/utils/prepareAttachedField';

/**
 * Represents an attachment that can be sent to the API.
 * Contains either new attachment data or destruction marker for existing attachments.
 */
export interface ProcessedAttachment {
  id?: string;
  file?: unknown;
  _destroy?: boolean;
  [key: string]: unknown;
}

/**
 * Normalizes attachment value from form to array format.
 * Form values can return either a single object or an array.
 *
 * @param imageValue - Raw attachment value from form (can be object or array)
 * @returns Array of attachment values
 */
export function normalizeAttachmentsToArray(
  imageValue: Values | Values[] | undefined,
): Values[] {
  if (!imageValue) return [];
  return Array.isArray(imageValue) ? imageValue : [imageValue];
}

/**
 * Checks if an attachment is marked for destruction.
 *
 * @param attachment - Attachment object to check
 * @returns True if attachment has _destroy flag set to true
 */
export function isAttachmentMarkedForDestruction(attachment: Values): boolean {
  // eslint-disable-next-line no-underscore-dangle
  return Boolean(attachment._destroy);
}

/**
 * Checks if an attachment is an existing attachment from the API.
 * Existing attachments have a 'urls' property with medium/original URLs.
 *
 * @param attachment - Attachment object to check
 * @returns True if attachment has urls property (indicating it's from API)
 */
export function isExistingAttachment(attachment: Values): boolean {
  return Boolean(attachment.urls);
}

/**
 * Processes current attachments from form values.
 * Filters out existing unchanged attachments and prepares new ones for upload.
 *
 * Shape normalization (strip `url` preview, skip items with `urls`, keep
 * `file`) is delegated to `prepareAttachedField`; this helper adds the
 * widget-specific concerns of `_destroy` early-exit and preserving the `id`
 * field when the form supplies one.
 *
 * @param currentAttachments - Array of attachments from form values
 * @returns Array of processed attachments ready for API
 */
export function processCurrentAttachments(
  currentAttachments: Values[],
): ProcessedAttachment[] {
  const attachmentsToSend: ProcessedAttachment[] = [];

  currentAttachments.forEach((attachment) => {
    if (!attachment) return;

    // Handle attachments marked for destruction
    if (isAttachmentMarkedForDestruction(attachment)) {
      attachmentsToSend.push({
        id: attachment.id as string,
        _destroy: true,
      });
      return;
    }

    const prepared = prepareAttachedField(attachment);
    // eslint-disable-next-line no-underscore-dangle
    if (!prepared || prepared._destroy) return;

    const processed: ProcessedAttachment = {
      ...(prepared as ProcessedAttachment),
    };
    if (attachment.id !== undefined) {
      processed.id = attachment.id as string;
    }
    attachmentsToSend.push(processed);
  });

  return attachmentsToSend;
}

/**
 * Finds original attachments that have been removed and marks them for destruction.
 *
 * @param originalAttachments - Array of attachments from the original widget (from API)
 * @param currentAttachments - Array of current attachments from form
 * @returns Array of destruction markers for removed attachments
 */
export function findRemovedAttachments(
  originalAttachments: Values[],
  currentAttachments: Values[],
): ProcessedAttachment[] {
  const removedAttachments: ProcessedAttachment[] = [];

  originalAttachments.forEach((originalAtt) => {
    const originalId = originalAtt?.id as string | undefined;
    if (!originalId) return;

    // Check if original attachment still exists in current attachments
    const stillExists = currentAttachments.some(
      (curr) => curr?.id === originalId,
    );

    if (!stillExists) {
      // Mark for destruction
      removedAttachments.push({
        id: originalId,
        _destroy: true,
      });
    }
  });

  return removedAttachments;
}

/**
 * Processes attachments for an Announcement widget.
 * Handles adding new attachments, keeping existing ones, and marking removed ones for destruction.
 *
 * @param formValues - Form values containing the announcement image field
 * @param originalWidget - Original widget from API (for detecting removed attachments)
 * @returns Array of processed attachments for API, or undefined if no changes
 */
export function processAnnouncementAttachments(
  formValues: Values,
  originalWidget?: Widget,
): ProcessedAttachment[] | undefined {
  // Get original attachments from API
  const originalAttachments =
    (originalWidget?.attachmentsAttached as Values[]) || [];

  // Get current attachments from form values (can be object or array)
  const imageValue = formValues?.[WidgetFieldNames.AnnouncementImage] as
    Values | Values[] | undefined;
  const currentAttachments = normalizeAttachmentsToArray(imageValue);

  // Process current attachments (new and marked for destruction)
  const processedCurrent = processCurrentAttachments(currentAttachments);

  // Find and mark removed attachments
  const removedAttachments = findRemovedAttachments(
    originalAttachments,
    currentAttachments,
  );

  // Combine all attachments to send
  const attachmentsToSend = [...processedCurrent, ...removedAttachments];

  return attachmentsToSend.length > 0 ? attachmentsToSend : undefined;
}

/**
 * Processes attachments for an ExternalLink widget.
 * ExternalLink attachments come from the widget template (icon image) and are not editable per-widget.
 * We need to extract them from the widget select option data and format them for the API.
 *
 * @param formValues - Form values containing the widget select field
 * @returns Array of processed attachments for API, or undefined if no attachments
 */
export function processExternalLinkAttachments(
  formValues: Values,
): ProcessedAttachment[] | undefined {
  // Get attachments from widget select option data
  const templateAttachments = _get(
    formValues,
    [WidgetFieldNames.Widget, 'data', 'attachmentsAttached'],
    [],
  ) as Values[];

  if (!templateAttachments || templateAttachments.length === 0) {
    return undefined;
  }

  // Same shape rules as processCurrentAttachments — new uploads only, existing
  // API rows are dropped — via the shared `prepareAttachedField` helper.
  const processedAttachments: ProcessedAttachment[] = [];
  templateAttachments.forEach((att) => {
    if (!att || isAttachmentMarkedForDestruction(att)) return;
    const prepared = prepareAttachedField(att);
    // eslint-disable-next-line no-underscore-dangle
    if (!prepared || prepared._destroy) return;
    const processed: ProcessedAttachment = {
      ...(prepared as ProcessedAttachment),
    };
    if (att.id !== undefined) {
      processed.id = att.id as string;
    }
    processedAttachments.push(processed);
  });

  return processedAttachments.length > 0 ? processedAttachments : undefined;
}
